import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';
import { userQueryKeys } from '@/lib/query-keys';
import { useEffect } from 'react';

export interface AIUsageData {
  isLoading: boolean;
  currentBalance: number;
  monthlyUsage: number;
  usagePercentage: number;
  remainingBalance: number;
  daysRemainingInCycle: number;
  currentCycleEnd: string | null;
  planName: string;
  error: string | null;
}

export const useAIUsageData = (): AIUsageData => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  // Listen for cache invalidation events from direct edge function calls
  useEffect(() => {
    const handleCacheInvalidation = (event: CustomEvent) => {
      if (!user?.id) return;
      
      const { immediate = true } = event.detail || {};
      
      if (immediate) {
        // Hard invalidation - immediate refetch
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.aiUsageData(user.id),
        });
        console.log('🔄 AI usage cache invalidated (HARD) via event listener');
      } else {
        // Soft invalidation - mark as stale
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.aiUsageData(user.id),
          refetchType: 'none',
        });
        console.log('🔄 AI usage cache invalidated (SOFT) via event listener');
      }
    };

    window.addEventListener('ai-usage-cache-invalidate', handleCacheInvalidation as EventListener);
    
    return () => {
      window.removeEventListener('ai-usage-cache-invalidate', handleCacheInvalidation as EventListener);
    };
  }, [user?.id, queryClient]);

  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: userQueryKeys.aiUsageData(user?.id),
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

        const { data, error } = await supabase
          .from('user_balance_with_usage')
          .select('*')
        .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('AI Usage Data Query Error:', error);
        throw new Error('Failed to load usage data');
      }

      return {
        currentBalance: data?.computed_balance || 0,
        monthlyUsage: data?.computed_monthly_usage || 0,
        usagePercentage: data?.usage_percentage || 0,
        remainingBalance: data?.remaining_balance || 0,
        daysRemainingInCycle: data?.days_remaining_in_cycle || 0,
        currentCycleEnd: data?.current_cycle_end || null,
        planName: data?.plan_name || 'Freemium',
      };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes - fresh enough for usage data
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch when dropdown opens
    retry: 2,
    retryDelay: 1000,
  });

  return {
    isLoading,
    currentBalance: data?.currentBalance || 0,
    monthlyUsage: data?.monthlyUsage || 0,
    usagePercentage: data?.usagePercentage || 0,
    remainingBalance: data?.remainingBalance || 0,
    daysRemainingInCycle: data?.daysRemainingInCycle || 0,
    currentCycleEnd: data?.currentCycleEnd || null,
    planName: data?.planName || 'Freemium',
    error: error ? (error instanceof Error ? error.message : 'Failed to load usage data') : null,
  };
}; 